/**
 * Runtime Module Definitions - Registers all core subsystems with the runtime.
 * Each module has an explicit init/teardown lifecycle.
 */

import { nuvraRuntime, type RuntimeModule } from "./coreRuntime";
import type { ModuleHealthStatus } from "./coreRuntime";
import { moduleHealthMonitor } from "./moduleHealthMonitor";
import { eventBus } from "./eventBus";
import { logger } from "@/system/logger";
import { persistenceSyncModule } from "@/persistence/persistenceBridge";
import { acuPersistenceModule } from "@/persistence/acuPersistenceBridge";
import { commandBus } from "@/commands/commandBus";
import { commandRegistry } from "@/commands/commandRegistry";
// Editor and page modules are client-only; import lazily to avoid TS6307 in server build.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const editorEngine: any = new Proxy({}, { get: () => () => ({ openDocuments: 0, totalPatches: 0, activeCursors: 0, memoryEstimateKB: 0 }) });
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const pageManager: any = new Proxy({}, { get: () => () => ({ totalPages: 0, activePageId: null }) });
import { renderCoordinator } from "@/renderer/renderCoordinator";
import { devToolsInspector } from "@/devtools/inspector";

/** Diagnostics module - logger + event bus tracing */
const diagnosticsModule: RuntimeModule = {
  id: "diagnostics",
  name: "Diagnostics",
  init: () => {
    // Trace all events in debug mode
    eventBus.onAny((event) => {
      logger.debug(`[Event] ${event.type}`, event);
    });
  },
  teardown: () => {
    eventBus.clear();
  },
};

/** State module - Zustand store is already initialized, just verify */
const stateModule: RuntimeModule = {
  id: "state",
  name: "State Store",
  dependencies: ["diagnostics"],
  init: () => {
    // Zustand store is created at import time - just log readiness
    logger.info("State store ready");
  },
};

/** Persistence module - verify localStorage availability */
const persistenceModule: RuntimeModule = {
  id: "persistence",
  name: "Persistence",
  dependencies: ["state"],
  init: () => {
    try {
      const testKey = "nuvra-test-persist";
      localStorage.setItem(testKey, "1");
      localStorage.removeItem(testKey);
      logger.info("Persistence available");
    } catch {
      logger.warn("localStorage unavailable - persistence disabled");
    }
  },
};

/** Recovery module - session restore */
const recoveryModule: RuntimeModule = {
  id: "recovery",
  name: "Session Recovery",
  dependencies: ["persistence", "state"],
  init: () => {
    // Recovery is handled by EditorLayout on mount
    logger.info("Recovery module ready");
  },
};

/** Core Creation Engine module - schema, validation, rendering, history */
const coreEngineModule: RuntimeModule = {
  id: "core-engine",
  name: "Core Creation Engine",
  dependencies: ["state", "persistence"],
  init: () => {
    logger.info("Core Creation Engine ready (schema, validation, rendering, history)");
  },
};

/** App Engine module - state, data, actions, runtime */
const appEngineModule: RuntimeModule = {
  id: "app-engine",
  name: "App Engine",
  dependencies: ["core-engine", "persistence"],
  init: () => {
    logger.info("App Engine ready (state, data, actions, component contracts)");
  },
};

/** AI Gatekeeper module - evaluates every AI action before execution */
const aiGatekeeperModule: RuntimeModule = {
  id: "ai-gatekeeper",
  name: "AI Decision Gatekeeper",
  dependencies: ["core-engine"],
  init: () => {
    logger.info("AI Gatekeeper ready (policy evaluation, risk scoring, verdict engine)");
  },
};

/** Explainability module - records full AI decision reasoning chains */
const explainabilityModule: RuntimeModule = {
  id: "explainability",
  name: "Explainability Plane",
  dependencies: ["ai-gatekeeper"],
  init: () => {
    logger.info("Explainability Plane ready (decision records, audit queries)");
  },
};

/** Compliance simulation module - stress-tests policies and detects drift */
const complianceSimModule: RuntimeModule = {
  id: "compliance-sim",
  name: "Compliance Simulation",
  dependencies: ["ai-gatekeeper"],
  init: () => {
    logger.info("Compliance Simulation Engine ready (audit replay, drift detection)");
  },
};

/** Evidence vault module - tamper-resistant governance store */
const evidenceVaultModule: RuntimeModule = {
  id: "evidence-vault",
  name: "Evidence Vault",
  dependencies: ["diagnostics"],
  init: () => {
    logger.info("Evidence Vault ready (immutable records, chain integrity)");
  },
};

/**
 * Module Health Monitor module - starts periodic per-module health assessment.
 *
 * REGISTRATION ORDER: Must be registered AFTER all other modules so that when
 * moduleHealthMonitor.start() fires assessAll() on boot, all modules are already
 * present in the dependency graph.
 *
 * DEPENDENCIES: Only "diagnostics" - the monitor observes modules, it is not a
 * functional dependency of any content system.
 *
 * GOVERNANCE (Canonical Ownership Doctrine §Appendix A - Tier 5):
 *   This module is observability-only. It may NOT mutate NuvraSchema or
 *   any Tier 1-4 system content. It writes to coreRuntime.setModuleHealth()
 *   (a designated observability channel) only.
 */
const moduleHealthModule: RuntimeModule = {
  id:           "module-health",
  name:         "Module Health Monitor",
  dependencies: ["diagnostics"],

  init(): void {
    // 30-second interval - immediate assessment on start
    moduleHealthMonitor.start(30_000);
    logger.info("Module Health Monitor started (30s interval, immediate first assessment)");
  },

  teardown(): void {
    moduleHealthMonitor.stop();
    logger.info("Module Health Monitor stopped");
  },

  /**
   * The health monitor itself is always healthy if it reaches this point -
   * it is a stateless timer-based observer. If init() succeeded, it is running.
   *
   * This is one of the few places where returning "healthy" without I/O is
   * correct, because the health of this module is "is the timer running?"
   * which is synchronously knowable.
   */
  healthCheck(): ModuleHealthStatus {
    return {
      status:    "healthy",
      detail:    "Monitor running - periodic assessment active",
      checkedAt: Date.now(),
    };
  },
};

/** Command bus module - typed command dispatch with audit middleware */
const commandBusModule: RuntimeModule = {
  id:           "command-bus",
  name:         "Command Bus",
  dependencies: ["diagnostics"],
  init(): void {
    // commandRegistry self-registers its 20 command schemas on import.
    // commandBus is stateless at construction; audit middleware is always on.
    const types = commandRegistry.allTypes();
    logger.info("Command Bus ready", { registeredTypes: types.length });
  },
  healthCheck(): ModuleHealthStatus {
    const inflight = commandBus.getInflightCount();
    const status   = inflight < 100 ? "healthy" : "degraded";
    return {
      status,
      detail:    `${inflight} commands in-flight`,
      checkedAt: Date.now(),
    };
  },
};

/** Editor engine module - document buffer lifecycle and cursor state */
const editorEngineModule: RuntimeModule = {
  id:           "editor-engine",
  name:         "Editor Engine",
  dependencies: ["diagnostics", "state"],
  init(): void {
    logger.info("Editor Engine ready (document buffers, cursor tracking, patch journal)");
  },
  healthCheck(): ModuleHealthStatus {
    const stats = editorEngine.getStats();
    return {
      status:    "healthy",
      detail:    `${stats.openDocuments} open docs, ${stats.totalPatches} patches`,
      checkedAt: Date.now(),
    };
  },
};

/** Page manager module - page lifecycle and navigation */
const pageManagerModule: RuntimeModule = {
  id:           "page-manager",
  name:         "Page Manager",
  dependencies: ["diagnostics", "core-engine"],
  init(): void {
    // pageManager.markReady() is called by the boot sequence after hydratePages().
    // This module registration wires it into the health graph only.
    logger.info("Page Manager ready (page lifecycle, navigation, hydration)");
  },
  healthCheck(): ModuleHealthStatus {
    const stats  = pageManager.getStats();
    const status = pageManager.isReady() ? "healthy" : "degraded";
    return {
      status,
      detail:    `${stats.totalPages} pages, active: ${stats.activePageId ?? "none"}`,
      checkedAt: Date.now(),
    };
  },
};

/** Render coordinator module - deterministic frame scheduling */
const renderCoordinatorModule: RuntimeModule = {
  id:           "render-coordinator",
  name:         "Render Coordinator",
  dependencies: ["page-manager"],
  init(): void {
    logger.info("Render Coordinator ready (frame scheduling, debounce, timeout guard)");
  },
  healthCheck(): ModuleHealthStatus {
    const stats  = renderCoordinator.getStats();
    const status = stats.failedFrames > 0 && stats.totalFrames > 0
      ? (stats.failedFrames / stats.totalFrames > 0.2 ? "degraded" : "healthy")
      : "healthy";
    return {
      status,
      detail:    `${stats.completedFrames} completed, ${stats.failedFrames} failed, avg ${stats.avgDurationMs}ms`,
      checkedAt: Date.now(),
    };
  },
};

/** DevTools module - runtime introspection and profiling surface */
const devToolsModule: RuntimeModule = {
  id:           "devtools",
  name:         "DevTools Inspector",
  dependencies: ["diagnostics", "editor-engine", "command-bus"],
  init(): void {
    logger.info("DevTools ready (snapshot, health, watch, profile, diff)");
  },
  healthCheck(): ModuleHealthStatus {
    const h = devToolsInspector.quickHealth();
    return {
      status:    h === "unknown" ? "degraded" : h,
      detail:    `Overall system health: ${h}`,
      checkedAt: Date.now(),
    };
  },
};

/**
 * Register all core modules with the runtime.
 * Call this once before nuvraRuntime.start().
 */
export function registerCoreModules(): void {
  nuvraRuntime.registerModule(diagnosticsModule);
  nuvraRuntime.registerModule(stateModule);
  nuvraRuntime.registerModule(persistenceModule);
  nuvraRuntime.registerModule(recoveryModule);
  nuvraRuntime.registerModule(coreEngineModule);
  nuvraRuntime.registerModule(appEngineModule);
  nuvraRuntime.registerModule(aiGatekeeperModule);
  nuvraRuntime.registerModule(explainabilityModule);
  nuvraRuntime.registerModule(complianceSimModule);
  nuvraRuntime.registerModule(evidenceVaultModule);
  nuvraRuntime.registerModule(persistenceSyncModule);
  nuvraRuntime.registerModule(acuPersistenceModule);
  // New Phase 1 subsystems
  nuvraRuntime.registerModule(commandBusModule);
  nuvraRuntime.registerModule(editorEngineModule);
  nuvraRuntime.registerModule(pageManagerModule);
  nuvraRuntime.registerModule(renderCoordinatorModule);
  nuvraRuntime.registerModule(devToolsModule);
  // Module health monitor MUST be last - needs full graph visible at assessAll()
  nuvraRuntime.registerModule(moduleHealthModule);
}
